import React, { useState, useEffect } from 'react';
import { ArrowLeft, Mail, MessageSquare, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import NavigationMenu from '../components/NavigationMenu';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [sending, setSending] = useState(false);
  const [myMessages, setMyMessages] = useState([]);
  const [expandedMessages, setExpandedMessages] = useState(new Set());
  
  const toggleMessage = (msgId) => {
    const newExpanded = new Set(expandedMessages);
    if (newExpanded.has(msgId)) {
      newExpanded.delete(msgId);
    } else {
      newExpanded.add(msgId);
    }
    setExpandedMessages(newExpanded);
  };
  
  // Auto-preencher com dados do usuário
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || ''
      }));
      fetchMyMessages();
    }
  }, [user]);
  
  const fetchMyMessages = async () => {
    try {
      const response = await axios.get(`${API}/support/messages/my`, { withCredentials: true });
      setMyMessages(response.data.messages || []);
      
      // Marcar como lidas
      await axios.post(`${API}/support/mark-read`, {}, { withCredentials: true });
    } catch (error) {
      // Ignorar erro
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.message) {
      toast.error(t('contact.messageRequired'));
      return;
    }
    
    try {
      setSending(true);
      
      await axios.post(`${API}/support/messages`, {
        subject: formData.subject || t('contact.noSubject'),
        message: formData.message
      }, { withCredentials: true });
      
      toast.success('✅ ' + t('contact.send') + '!');
      setFormData(prev => ({ ...prev, subject: '', message: '' }));
      fetchMyMessages(); // Atualizar histórico
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50">
      <NavigationMenu />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Button onClick={() => navigate(-1)} variant="ghost" className="mb-6">
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('common.back')}
        </Button>

        <div className="mb-8 text-center">
          <h1 className="text-4xl font-bold text-amber-900 mb-2">
            <MessageSquare className="w-10 h-10 inline-block mr-3" />
            {t('contact.title')}
          </h1>
          <p className="text-gray-600">{t('contact.description')}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <Card className="p-6 border-2 border-amber-100">
            <h3 className="font-semibold text-amber-900 mb-3 flex items-center gap-2">
              <Mail className="w-5 h-5" />
              {t('contact.emailCard')}
            </h3>
            <p className="text-gray-700 mb-2">support@mai-recipes.com</p>
            <p className="text-sm text-gray-500">{t('contact.responsesIn24h')}</p>
          </Card>

          <Card className="p-6 border-2 border-amber-100">
            <h3 className="font-semibold text-amber-900 mb-3">{t('contact.scheduleCard')}</h3>
            <p className="text-gray-700">{t('contact.mondayToFriday')}</p>
            <p className="text-gray-700">{t('contact.hours')}</p>
          </Card>
        </div>

        {/* Histórico de Mensagens PRIMEIRO */}
        {myMessages.length > 0 && (
          <Card className="p-6 border-2 border-purple-100 mb-6">
            <h2 className="text-2xl font-semibold text-purple-900 mb-4">
              📬 {t('contact.myMessages')} ({myMessages.length})
            </h2>
            
            <div className="space-y-3">
              {myMessages.map((msg) => (
                <Card 
                  key={msg.id} 
                  className={`p-4 cursor-pointer transition-all hover:shadow-md ${
                    msg.status === 'respondido' ? 'bg-green-50 border-green-300' : 
                    'bg-red-50 border-red-300'
                  }`}
                  onClick={() => toggleMessage(msg.id)}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900">{msg.subject || t('contact.noSubject')}</p>
                      <p className="text-xs text-gray-500">
                        {new Date(msg.created_at).toLocaleString('pt-BR')}
                      </p>
                    </div>
                    <Badge className={
                      msg.status === 'novo' ? 'bg-red-600' :
                      msg.status === 'respondido' ? 'bg-green-600' :
                      'bg-gray-600'
                    }>
                      {msg.status === 'respondido' ? `✅ ${t('contact.responded')}` : `⏳ ${t('contact.waiting')}`}
                    </Badge>
                  </div>
                  
                  <p className="text-sm text-gray-700 mb-2">{msg.message}</p>
                  
                  {/* Respostas do Admin - Expansível */}
                  {msg.responses && msg.responses.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-300">
                      {expandedMessages.has(msg.id) ? (
                        <div>
                          <p className="text-sm font-semibold text-green-700 mb-2">
                            ✅ {t('contact.responseFromSupport')}:
                          </p>
                          {msg.responses.map((resp, idx) => (
                            <div key={idx} className="bg-white p-3 rounded mb-2 border border-green-200">
                              <p className="text-sm text-gray-700">{resp.response}</p>
                              <p className="text-xs text-gray-500 mt-1">
                                {new Date(resp.created_at).toLocaleString('pt-BR')}
                              </p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-green-700 font-medium">
                          💬 {msg.responses.length} {msg.responses.length === 1 ? t('contact.response') : t('contact.responses')} - {t('contact.clickToSeeResponses')}
                        </p>
                      )}
                    </div>
                  )}
                </Card>
              ))}
            </div>
          </Card>
        )}

        {/* Formulário EMBAIXO */}
        <Card className="p-6 border-2 border-amber-100">
          <h2 className="text-2xl font-semibold text-amber-900 mb-6">{t('contact.sendMessage')}</h2>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('contact.name')}</label>
              <Input
                type="text"
                value={formData.name}
                readOnly
                className="bg-gray-50"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('contact.email')}</label>
              <Input
                type="email"
                value={formData.email}
                readOnly
                className="bg-gray-50"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('contact.subject')}</label>
              <Input
                type="text"
                value={formData.subject}
                onChange={(e) => setFormData({...formData, subject: e.target.value})}
                placeholder={t('contact.subjectPlaceholder')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('contact.messageRequired')}</label>
              <Textarea
                value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value})}
                placeholder={t('contact.messagePlaceholder')}
                rows={6}
                required
              />
            </div>

            <Button
              type="submit"
              disabled={sending}
              className="w-full bg-orange-600 hover:bg-orange-700 text-white"
            >
              {sending ? t('contact.sending') : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  {t('contact.send')}
                </>
              )}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default ContactPage;

